/**
 * Vercel SDK v6 ChatTransport over the existing SyncTransport WebSocket.
 *
 * The transport bridges Vercel SDK v6 `useChat({ transport })` to our
 * binary-envelope WS protocol:
 *
 *   sendMessages (every turn)             → KIND_AGENT_RUN, no history body
 *                                           (Variant B, §7.3: nexus fills
 *                                           StartRun.history from rows; the
 *                                           client never sends history)
 *   sendApprovalResponse                  → KIND_AGENT_APPROVE
 *   KIND_AGENT_EVENT body (raw chunk)     → enqueue as UIMessageChunk
 *   KIND_AGENT_EVENT body (turn-complete) → close stream (turn boundary)
 *   KIND_AGENT_DONE (session-level)       → fire onConversationDone + clear
 *                                           runByChatId (does NOT close the
 *                                           per-turn stream — that already
 *                                           happened on turn-complete)
 *
 * Wire-format references:
 *   - apps/nexus/nexus/gateway/events.rs:9-83 — envelope-body shapes
 *   - apps/nexus/nexus/gateway/handler.rs:347-410 — 3-arm proto event match
 *   - node_modules ai@6.0.116 src/ui/chat-transport.ts:15-83 — ChatTransport interface
 *   - node_modules ai@6.0.116 src/ui-message-stream/ui-message-chunks.ts:16-180 — chunk shapes
 */

import * as Sentry from '@sentry/react';
import type {
  ChatTransport,
  UIMessage,
  UIMessageChunk,
} from 'ai';
import {
  encodeEnvelope,
  encodeJson,
  bytesToUuid,
  type Envelope,
} from '@/lib/sync/envelope';
import {
  KIND_AGENT_RUN,
  KIND_AGENT_EVENT,
  KIND_AGENT_APPROVE,
  KIND_AGENT_DONE,
  KIND_AGENT_TOOL,
} from '@/lib/sync/envelope-kinds';
import type { SyncTransport } from '@/lib/sync/sync-transport';
import type { EditRequest } from '@/lib/ai-composer/types';
import { openEditActionStream } from '@/lib/ai-composer/edit-action-protocol';
import { invalidateCreditBalance } from '@/lib/billing/use-subscription';
import { useConversationRegistryStore } from '../stores/use-conversation-registry-store';
import { useWorkspaceStore } from '@/stores/use-workspace-store';

/**
 * Build a KIND_AGENT_TOOL envelope for a conversation-less editor edit-action run.
 * The corrId is the runId (UUID string); the body carries the full EditRequest + blockContent.
 */
export function buildEditActionFrame(
  runId: string,
  req: EditRequest,
  blockContent: string,
): Uint8Array {
  const body = {
    action: req.action,
    entityKind: req.entityKind,
    entityId: req.entityId,
    target: req.target,
    params: req.params ?? {},
    blockContent,
  };
  return encodeEnvelope({
    kind: KIND_AGENT_TOOL,
    corrId: runId,
    json: encodeJson(body),
  });
}

export interface AssistantTransportContextEntity {
  type: 'document' | 'collection' | 'event' | 'canvas';
  id: string;
  title?: string;
  relevance: 'focused' | 'pinned' | 'ambient';
}

export interface AssistantTransportContextItem {
  type: string;
  id: string;
  title: string;
  relevance?: string;
  content?: string;
  metadata?: Record<string, string>;
}

export interface AssistantTransportContext {
  activeEntityId: string | null;
  activeEntityTitle?: string | null;
  activeEntities?: AssistantTransportContextEntity[];
  contextItems?: AssistantTransportContextItem[];
  model?: string;
}

export interface AssistantTransportOptions {
  /** The shared SyncTransport instance from SyncSystemContext. */
  syncTransport: SyncTransport;
  /** Pure function: produces the current context for an outgoing message. Called per sendMessages call. */
  getContext: () => AssistantTransportContext;
  /** Fired exactly once per turn end (KIND_AGENT_EVENT body type "turn-complete"). Optional. */
  onTurnComplete?: (stats: { iterations: number; toolsCalled: number; hasPendingApproval: boolean }) => void;
  /** Fired exactly once per conversation end (KIND_AGENT_DONE envelope). Optional. */
  onConversationDone?: (stats: { iterations: number; toolsCalled: number; cancelled: boolean }) => void;
  /**
   * Fired connection-scoped when a cortex approval chunk
   * ({ type: 'tool-approval-request', approvalId, toolCallId }) arrives for an
   * owned run. Carries the owning chatId so the consumer can raise a "needs you"
   * badge for a BACKGROUND conversation whose tab is unmounted (no per-turn
   * stream consumer reads it). Optional.
   */
  onApprovalRequested?: (chatId: string) => void;
  /**
   * Fired exactly once when a fresh chat's first turn mints its conversation/run
   * id, so consumers like the overlay can record which conversation is now active
   * (e.g. by calling setCurrent(conversationId) so expand-to-full-tab can
   * continue that conversation instead of opening a blank new one).
   *
   * Only fires after a successful send (not when the transport is disconnected).
   * Does NOT fire on follow-up turns (the run id is already established), and
   * does NOT fire when presetRun was called before the first send (the run id
   * was provided externally, not minted here). Optional.
   */
  onConversationStarted?: (conversationId: string) => void;
}

type TurnCompleteBody = {
  type: 'turn-complete';
  iterations?: number;
  toolsCalled?: number;
  hasPendingApproval?: boolean;
};

/**
 * Test whether a 16-byte corrId matches a hyphen-stripped 32-char hex runId.
 * Inlined to avoid an extra allocation per frame; the WS firehose can deliver
 * hundreds of frames per second under load.
 */
function corrIdMatches(corrIdBytes: Uint8Array, runIdHex: string): boolean {
  if (corrIdBytes.length !== 16) return false;
  for (let i = 0; i < 16; i++) {
    const high = parseInt(runIdHex.charAt(i * 2), 16);
    const low = parseInt(runIdHex.charAt(i * 2 + 1), 16);
    if (((high << 4) | low) !== corrIdBytes[i]) return false;
  }
  return true;
}

/**
 * Extract the text of the most-recent user message in the chat history.
 * Returns '' when no user message is present.
 */
function extractLastUserText(messages: ReadonlyArray<UIMessage>): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'user') continue;
    const parts = msg.parts ?? [];
    for (const part of parts) {
      if (part.type === 'text') {
        return (part as { type: 'text'; text: string }).text ?? '';
      }
    }
  }
  return '';
}

/**
 * Read the AI SDK's per-request `options.body` (typed `object` by the SDK) and
 * pull out a `contextItems` array if present. Defensive: only items that are
 * objects with a string `type` survive.
 */
function extractBodyContextItems(body: unknown): AssistantTransportContextItem[] {
  if (!body || typeof body !== 'object') return [];
  const items = (body as { contextItems?: unknown }).contextItems;
  if (!Array.isArray(items)) return [];
  return items.filter((it): it is AssistantTransportContextItem => {
    if (!it || typeof it !== 'object') return false;
    const o = it as Record<string, unknown>;
    // Validate every field the type declares REQUIRED so the predicate doesn't
    // over-promise: an item missing id/title would otherwise pass and be spread
    // verbatim into the frame's contextItems.
    return (
      typeof o.type === 'string' &&
      typeof o.id === 'string' &&
      typeof o.title === 'string'
    );
  });
}

/**
 * Custom `ChatTransport` that bridges Vercel SDK v6 useChat to SyncTransport.
 */
export class AssistantTransport implements ChatTransport<UIMessage> {
  private readonly syncTransport: SyncTransport;
  private readonly getContext: () => AssistantTransportContext;
  private readonly onTurnComplete?: AssistantTransportOptions['onTurnComplete'];
  private readonly onConversationDone?: AssistantTransportOptions['onConversationDone'];
  private readonly onApprovalRequested?: AssistantTransportOptions['onApprovalRequested'];
  private readonly onConversationStarted?: AssistantTransportOptions['onConversationStarted'];

  /** Per-chatId run state. Populated on first sendMessages call; persisted across follow-up turns. */
  private readonly runByChatId = new Map<string, string>();

  /**
   * Chat IDs with a turn currently streaming (a per-turn ReadableStream is open
   * and has not yet hit turn-complete / done / abort). sendMessages rejects a
   * second turn while one is in progress for the same chatId — the client-side
   * replacement for the server pending_messages queue removed in the no-continue
   * milestone (every follow-up turn is a fresh RUN; standard ChatGPT/Claude
   * "input disabled while running" UX).
   *
   * This transport-local set is the send-guard by design: it tracks the precise
   * per-turn ReadableStream lifetime (open → turn-complete / done / abort).
   * The connection-scoped registry also exposes a `running` status derived from
   * the firehose; the two signals are complementary — the registry is for
   * badge rendering and grace eviction, this set is the send-side backstop.
   *
   * Named `activeTurnChatIds` to avoid collision with the public
   * `runningChatIds()` method used for reconnect reconciliation.
   */
  private readonly activeTurnChatIds = new Set<string>();

  /**
   * Chat IDs that have completed at least one successful send. Drives the
   * once-per-conversation onConversationStarted notification regardless of
   * whether the runId was minted or preset. Cleared by clearRunId so a fresh
   * conversation on the same key fires again.
   */
  private readonly startedChatIds = new Set<string>();

  /**
   * The currently-open per-turn ReadableStream for each streaming chatId. Set
   * when openTurnStream constructs a stream, cleared when that stream closes by
   * any path (cleanupListeners). Lets resumeRunIfLive be idempotent: a resume
   * for a chat that already has an open stream returns THAT stream instead of
   * opening a second firehose subscription. This holds a stream HANDLE, not
   * message content — no parallel messages buffer (Chat.messages stays the
   * single source of truth).
   */
  private readonly openTurnStreams = new Map<string, ReadableStream<UIMessageChunk>>();

  /**
   * Session-level KIND_AGENT_DONE handler unsubscribe. Registered once in the
   * constructor and lives for the lifetime of the AssistantTransport instance.
   *
   * DONE is a CONVERSATION-end signal, not a turn-end signal — it may arrive
   * AFTER a turn-complete on the SAME corrId (cortex sends turn-complete when
   * the agent parks at idle, then DONE when the conversation closes). The
   * per-turn onFrame handler is unsubscribed by turn-complete, so DONE
   * delivery requires a separate session-scoped listener.
   */
  private readonly disposeDoneHandler: () => void;

  /**
   * Connection-scoped KIND_AGENT_EVENT approval-request listener unsubscribe.
   * Sibling of disposeDoneHandler: survives per-turn stream unsubscribe so a
   * BACKGROUND run's approval interrupt still raises a badge with no UI mounted.
   */
  private readonly disposeApprovalHandler: () => void;

  /**
   * Guards against double-dispose. Under React StrictMode the useEffect cleanup
   * fires on the dev unmount → release → refCount 0 → dispose(). If dispose() were
   * called again (e.g. a second unmount race or a future code path), a second
   * invocation must be a safe no-op. Production does not use StrictMode
   * (apps/lumen/src/main.tsx), so this is a dev-safety + defensive-depth guard.
   */
  private disposed = false;

  constructor(options: AssistantTransportOptions) {
    this.syncTransport = options.syncTransport;
    this.getContext = options.getContext;
    this.onTurnComplete = options.onTurnComplete;
    this.onConversationDone = options.onConversationDone;
    this.onApprovalRequested = options.onApprovalRequested;
    this.onConversationStarted = options.onConversationStarted;

    // Register the session-level DONE listener once. Cleaned up via dispose().
    this.disposeDoneHandler = this.syncTransport.onFrame((envelope: Envelope) => {
      if (envelope.kind !== KIND_AGENT_DONE) return;
      this.handleSessionDoneFrame(envelope);
    });

    // Connection-scoped approval-request listener — see handleApprovalRequestFrame.
    this.disposeApprovalHandler = this.syncTransport.onFrame((envelope: Envelope) => {
      if (envelope.kind !== KIND_AGENT_EVENT) return;
      this.handleApprovalRequestFrame(envelope);
    });
  }

  /** Public: returns the active runId for a given chatId, or undefined. Used by useCancelRun. */
  getRunId(chatId: string): string | undefined {
    return this.runByChatId.get(chatId);
  }

  /** Public: erase the runId for a chatId — caller is responsible for ending any live stream first. */
  clearRunId(chatId: string): void {
    this.runByChatId.delete(chatId);
    this.activeTurnChatIds.delete(chatId);
    this.openTurnStreams.delete(chatId);
    this.startedChatIds.delete(chatId);
  }

  /**
   * Enumerate every chat with an active run, for reconnect reconciliation
   * (design §8.5). `runByChatId` holds exactly the live runs: populated on
   * send, deleted on the session DONE frame (handleSessionDoneFrame). The
   * conversationId IS the runId (design §4 — one key across all layers).
   *
   * Note: liveness authority is the connection-scoped ConversationRegistry
   * store (use-conversation-registry-store), not this method. This is a routing
   * helper that maps a running conversation to the
   * transport's resume path. The coordinator iterates whatever the REGISTRY
   * marks `running` and uses this only to build the chatId→conversationId map.
   */
  runningChatIds(): Array<{ chatId: string; conversationId: string }> {
    const out: Array<{ chatId: string; conversationId: string }> = [];
    for (const [chatId, runId] of this.runByChatId.entries()) {
      out.push({ chatId, conversationId: runId });
    }
    return out;
  }

  /**
   * Reconnect-mid-run resume (design §8.5). After a WS drop+reconnect, the
   * coordinator calls this per chat that the connection-scoped
   * ConversationRegistry (single liveness owner) marks `running`:
   *   - run still live (runByChatId has the entry, no DONE seen yet): re-open
   *     the per-turn firehose stream so post-reconnect chunks land in the same
   *     Chat. The coordinator first merges the refetched persisted tail
   *     (reconcileMessages) so no chunk lost on the dead socket leaves a gap.
   *   - run already completed while disconnected (DONE cleared runByChatId):
   *     return resumed=false; the coordinator reconciles the Chat to the
   *     persisted (done) transcript and flips status to 'done'. NO stream
   *     re-opens — the run ended.
   *
   * Buffer-free (design §8.2): the transport does NOT hold Chat.messages. The
   * merge happens in the coordinator via reconcileMessages; this method only
   * owns the firehose subscription.
   */
  resumeRunIfLive(chatId: string): { resumed: boolean; stream?: ReadableStream<UIMessageChunk> } {
    const runId = this.runByChatId.get(chatId);
    if (runId === undefined) {
      // Run already completed (DONE deleted the entry) — nothing live to resume.
      return { resumed: false };
    }
    // Idempotent: if a turn stream is already open for this chat (e.g. the SDK's
    // reconnectToStream already resumed it, or sendMessages is still streaming),
    // return the EXISTING stream rather than opening a second openTurnStream.
    // Two firehose subscriptions on the same runId would double-process every
    // chunk and leak a handler — the coordinator and the SDK reconnect path must
    // share exactly ONE stream per live run.
    const existing = this.openTurnStreams.get(chatId);
    if (existing) {
      return { resumed: true, stream: existing };
    }
    // No live stream yet — arm the send-while-running guard and open exactly one
    // stream on the (now reconnected) shared firehose.
    this.activeTurnChatIds.add(chatId);
    const stream = this.openTurnStream(chatId, runId, undefined);
    return { resumed: true, stream };
  }

  /**
   * Bind a chatId to an existing conversation id so the next turn's RUN corrId
   * matches the conversation. History is NOT sent — nexus fills it from rows
   * (Variant B, §7.3). No resume flag needed.
   *
   * Called both by AssistantChatProvider on mount (chatId IS the conversationId,
   * §7.4) and by loadConversation when reopening a saved conversation.
   */
  presetRun(chatId: string, conversationId: string): void {
    this.runByChatId.set(chatId, conversationId);
  }

  /**
   * Tear down all session-level resources. MUST be called when the surrounding
   * hook unmounts (see useAssistantChat's useEffect cleanup) — otherwise the
   * DONE listener leaks for the lifetime of the SyncTransport.
   *
   * Idempotent: safe to call multiple times. Subsequent calls after the first
   * are no-ops — the session-level DONE handler is only unregistered once.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.disposeDoneHandler();
    this.disposeApprovalHandler();
  }

  /**
   * Delegates to the standalone openEditActionStream protocol helper.
   * The logic lives in lib/ai-composer/edit-action-protocol so it can be used
   * from router scope without chat/conversation lifetime coupling.
   */
  async openEditActionStream(
    runId: string,
    req: EditRequest,
    blockContent: string,
  ): Promise<ReadableStream<Uint8Array>> {
    return openEditActionStream(this.syncTransport, runId, req, blockContent);
  }

  async sendMessages(
    options: Parameters<ChatTransport<UIMessage>['sendMessages']>[0],
  ): Promise<ReadableStream<UIMessageChunk>> {
    const { chatId, messages, body } = options;

    // Block send-while-running (M1c): with KIND_AGENT_CONTINUE and the server
    // pending_messages queue gone, a second turn must NOT fire while the first
    // is still streaming. Reject before minting/sending anything. (The UI also
    // disables the input on `running` status — this is the transport-level
    // backstop.) The transport-local in-flight set is the send-guard by design —
    // it tracks the per-turn ReadableStream lifetime, which is more precise than
    // the registry's firehose-derived status for the purposes of blocking a
    // second send on the same chatId.
    if (this.activeTurnChatIds.has(chatId)) {
      throw new Error('conversation is already running — send is busy');
    }

    const userText = extractLastUserText(messages);

    const existingRunId = this.runByChatId.get(chatId);
    const isFirstTurn = existingRunId === undefined;
    const runId: string = isFirstTurn ? crypto.randomUUID() : existingRunId;
    if (isFirstTurn) {
      this.runByChatId.set(chatId, runId);
    }

    const context = this.getContext();
    const bodyItems = extractBodyContextItems(body);

    // Creator-owns-id (design §6.4): forward the SDK-minted user-message id
    // verbatim. The SDK mints it at sendMessage time on the last user message.
    // nexus reads body.messageId → StartRun.message_id; cortex stamps it as the
    // new user ChatMessage's ui_id; nexus persists it as conversation_messages.id.
    // No re-generation, no positional id. If no user message is found (empty
    // messages), omit the field — cortex mints a stable random fallback (never
    // positional).
    const lastUserMessageId = (() => {
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'user') return messages[i].id;
      }
      return undefined;
    })();

    // Variant B (§7.3): EVERY turn is a plain KIND_AGENT_RUN with NO history.
    // nexus reads the prior transcript from conversation_messages rows at RUN
    // time and fills StartRun.history. The client is a thin renderer — it never
    // sends history, so it can never send it wrong (kills the v1 double-persist).
    const frame = this.buildAgentRunFrame(runId, userText, context, lastUserMessageId, bodyItems);

    const ok = this.syncTransport.send(frame);
    if (!ok) {
      if (isFirstTurn) {
        this.runByChatId.delete(chatId);
      }
      throw new Error('not_connected');
    }

    // Mark the chat as currently running.
    this.activeTurnChatIds.add(chatId);

    // The server never echoes KIND_AGENT_RUN back — only EVENT and DONE travel
    // S->C — so nothing inbound can report a run as started. Saying so here, at
    // the point the frame actually goes out, is what makes 'running' reachable
    // at all; turn-complete and needs-approval still arrive over the wire.
    useConversationRegistryStore.getState().markRunning(runId);

    // Notify on the FIRST successful send for this chat — whether the runId was
    // freshly minted (overlay) or preset (presetRun for a UUID chatId). Callers
    // record the conversation as "started/active" (e.g. the ambient pointer).
    // Tracked per-chat so follow-up turns never re-fire it.
    if (!this.startedChatIds.has(chatId)) {
      this.startedChatIds.add(chatId);
      this.onConversationStarted?.(runId);
    }

    return this.openTurnStream(chatId, runId, options.abortSignal);
  }

  async reconnectToStream(
    options: Parameters<ChatTransport<UIMessage>['reconnectToStream']>[0],
  ): Promise<ReadableStream<UIMessageChunk> | null> {
    // The Vercel SDK calls this to re-attach a Chat to its in-flight run after
    // a transient transport blip. We resume the firehose for the run if it is
    // still live; the persisted-tail merge is driven by the reconnect coordinator
    // on the SyncTransport status edge (design §8.5). Returns null when the run
    // already completed (the coordinator reconciles to the persisted 'done'
    // transcript).
    const { chatId } = options;
    const result = this.resumeRunIfLive(chatId);
    return result.stream ?? null;
  }

  /**
   * Send an approval decision for a pending tool-approval-request chunk.
   * The surrounding hook (useAssistantChat) wires this to
   * useChat's addToolApprovalResponse({ id, approved, reason? }).
   */
  sendApprovalResponse(chatId: string, payload: { approvalId: string; approved: boolean }): void {
    const runId = this.runByChatId.get(chatId);
    if (!runId) {
      // No active run — silently drop. The SDK can fire approval responses
      // during edge race conditions; surfacing an error would crash the UI.
      console.warn('[AssistantTransport] sendApprovalResponse: no runId for chatId', chatId, payload);
      return;
    }
    const action = payload.approved ? 'approve' : 'cancel';
    // Diagnostic: log the cancel-path so we can correlate with cortex
    // approve_node_resumed log lines. Helps verify the click actually
    // reaches the wire — if this log fires but cortex shows no resume,
    // the issue is in the gRPC transport / sync layer.
    console.info(
      '[AssistantTransport] sendApprovalResponse',
      { chatId, runId, approvalId: payload.approvalId, action },
    );
    const frame = encodeEnvelope({
      kind: KIND_AGENT_APPROVE,
      corrId: runId,
      json: encodeJson({ approvalId: payload.approvalId, action }),
    });
    const ok = this.syncTransport.send(frame);
    if (!ok) {
      console.warn(
        '[AssistantTransport] approval send failed — transport not connected',
        { chatId, runId, approvalId: payload.approvalId, action },
      );
    } else {
      console.info('[AssistantTransport] approval frame sent', { runId, approvalId: payload.approvalId });
    }
  }

  // ── private — frame builders ─────────────────────────────────────────────

  private buildAgentRunFrame(
    runId: string,
    message: string,
    context: AssistantTransportContext,
    messageId?: string,
    extraItems: AssistantTransportContextItem[] = [],
  ): Uint8Array {
    const body: Record<string, unknown> = { context };
    // cortex reads model from BOTH body.model AND body.context.model;
    // see sendAgentRun.ts for the legacy precedent — keep both paths
    // populated until the cortex consumer is normalized in a future milestone.
    if (context.model) body.model = context.model;
    if (message) body.message = message;
    // Creator-owns-id (design §6.4): forward the SDK-minted user-message id.
    // nexus reads body.messageId → StartRun.message_id; cortex stamps it as
    // the new user ChatMessage's ui_id; persisted as conversation_messages.id.
    if (messageId) body.messageId = messageId;
    const items = [...this.mergeContextItems(context), ...extraItems];
    if (items.length > 0) body.contextItems = items;
    return encodeEnvelope({
      kind: KIND_AGENT_RUN,
      corrId: runId,
      json: encodeJson(body),
    });
  }

  private mergeContextItems(
    context: AssistantTransportContext,
  ): AssistantTransportContextItem[] {
    const fromEntities = (context.activeEntities ?? []).map(
      (e): AssistantTransportContextItem => ({
        type: e.type,
        id: e.id,
        title: e.title ?? '',
        relevance: e.relevance,
      }),
    );
    const fromContext = context.contextItems ?? [];
    return [...fromEntities, ...fromContext];
  }

  // ── private — per-turn stream ─────────────────────────────────────────────

  /**
   * Open a ReadableStream<UIMessageChunk> for the in-flight turn.
   *
   * Subscribes to SyncTransport.onFrame, filters by corrId === runId,
   * dispatches two KIND_AGENT_EVENT body shapes:
   *   1. Raw UIMessageChunk JSON → enqueue to stream
   *   2. turn-complete body → close stream + fire onTurnComplete + unsubscribe
   *
   * KIND_AGENT_DONE is NOT handled here — see `handleSessionDoneFrame` for the
   * session-level listener that survives per-turn unsubscribe.
   */
  private openTurnStream(chatId: string, runId: string, abortSignal: AbortSignal | undefined): ReadableStream<UIMessageChunk> {
    const runIdHex = runId.replace(/-/g, '');
    let unsubscribe: (() => void) | null = null;
    let abortListener: (() => void) | null = null;
    const decoder = new TextDecoder();

    /**
     * Atomically unsubscribe the onFrame listener and remove the abort listener.
     * Must be called on EVERY close path — consumer cancel (cancelStream),
     * producer turn-complete (completeTurn), and abort signal. (KIND_AGENT_DONE
     * is handled session-level and does NOT call this — see handleSessionDoneFrame.)
     * Idempotent: safe to call multiple times.
     *
     * Also clears the chat's `running` flag (M1c send-while-running guard): once
     * the turn stream closes by ANY path the chat is no longer running, so a
     * follow-up send is permitted again.
     */
    const cleanupListeners = () => {
      this.activeTurnChatIds.delete(chatId);
      this.openTurnStreams.delete(chatId);
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
      if (abortListener && abortSignal) {
        abortSignal.removeEventListener('abort', abortListener);
        abortListener = null;
      }
    };

    /**
     * Close the stream and clean up listeners atomically. Used by all
     * producer-initiated close paths so neither step is forgotten.
     */
    const finishTurn = (controller: ReadableStreamDefaultController<UIMessageChunk>) => {
      cleanupListeners();
      try { controller.close(); } catch { /* already closed */ }
    };

    // Arrow functions preserve `this` from the enclosing method context.
    const startStream = (controller: ReadableStreamDefaultController<UIMessageChunk>) => {
      unsubscribe = this.syncTransport.onFrame((envelope: Envelope) => {
        // Filter: only frames whose corrId matches THIS run.
        if (!corrIdMatches(envelope.corrId, runIdHex)) return;

        if (envelope.kind === KIND_AGENT_EVENT) {
          this.handleAgentEventFrame(envelope, runId, controller, decoder, finishTurn);
          return;
        }

        // DONE closes the per-turn stream too (in case it arrives WITHOUT a
        // preceding turn-complete — e.g., the user cancels mid-turn or closes
        // the conversation). The session-level handler registered in the
        // constructor fires onConversationDone independently — see
        // handleSessionDoneFrame for why the listener split is necessary.
        if (envelope.kind === KIND_AGENT_DONE) {
          finishTurn(controller);
          return;
        }

        // Other kinds (auth, ops, etc.) are not our business — ignore.
      });

      if (abortSignal) {
        if (abortSignal.aborted) {
          finishTurn(controller);
          return;
        }
        abortListener = () => {
          finishTurn(controller);
        };
        abortSignal.addEventListener('abort', abortListener);
      }
    };

    // Consumer-cancel path: ReadableStream calls this when the reader cancels.
    const cancelStream = cleanupListeners;

    const stream = new ReadableStream<UIMessageChunk>({
      start: startStream,
      cancel: cancelStream,
    });
    // Track the open stream so resumeRunIfLive can return THIS one instead of
    // opening a duplicate firehose subscription. Cleared in cleanupListeners on
    // every close path.
    this.openTurnStreams.set(chatId, stream);
    return stream;
  }

  /** Dispatch a KIND_AGENT_EVENT envelope to the active turn's stream. */
  private handleAgentEventFrame(
    envelope: Envelope,
    runId: string,
    controller: ReadableStreamDefaultController<UIMessageChunk>,
    decoder: TextDecoder,
    finishTurn: (controller: ReadableStreamDefaultController<UIMessageChunk>) => void,
  ): void {
    if (envelope.json.length === 0) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(decoder.decode(envelope.json));
    } catch (err) {
      Sentry.captureException(err, { extra: { context: 'AssistantTransport.json_parse', runId } });
      return;
    }
    if (typeof parsed !== 'object' || parsed === null || !('type' in parsed)) {
      console.error('[AssistantTransport] malformed KIND_AGENT_EVENT body (no type field)', { body: parsed });
      Sentry.captureMessage(
        'AssistantTransport: malformed KIND_AGENT_EVENT body (no type field)',
        { level: 'error', extra: { body: parsed } },
      );
      return;
    }
    const body = parsed as { type: string };

    // Detect the flat turn-complete shape BEFORE treating it as a Vercel chunk.
    // The turn-complete body is: {"type":"turn-complete","iterations":N,"toolsCalled":N,"hasPendingApproval":bool}
    // This is NOT a valid UIMessageChunk — it must NOT be enqueued.
    if (body.type === 'turn-complete') {
      this.completeTurn(controller, body as TurnCompleteBody, finishTurn);
      return;
    }

    // All other UIMessageChunk shapes — including data-* custom chunks —
    // are valid Vercel chunks. Enqueue verbatim.
    controller.enqueue(body as UIMessageChunk);
  }

  /**
   * Session-level KIND_AGENT_DONE handler. Registered once in the constructor
   * (via the syncTransport.onFrame returned dispose function stored in
   * `disposeDoneHandler`). Lives for the lifetime of the AssistantTransport
   * instance, NOT tied to any per-turn ReadableStream.
   *
   * Why session-level: turn-complete unsubscribes the per-turn onFrame listener
   * (so per-turn handler leaks are bounded — see C1). But DONE may follow
   * turn-complete on the SAME corrId, so it needs an independent listener that
   * survives turn-complete cleanup.
   */
  private handleSessionDoneFrame(envelope: Envelope): void {
    const corrIdHex = bytesToUuid(envelope.corrId);
    // Only act on DONE frames whose corrId matches a runId we own. Other apps
    // sharing the WS may have their own DONE-shaped frames — ignore them.
    let matchedChatId: string | undefined;
    for (const [chatId, rid] of this.runByChatId.entries()) {
      if (rid === corrIdHex) {
        matchedChatId = chatId;
        break;
      }
    }
    if (matchedChatId === undefined) return;

    let body: { iterations?: number; toolsCalled?: number; cancelled?: boolean } = {};
    if (envelope.json.length > 0) {
      try {
        const decoder = new TextDecoder();
        body = JSON.parse(decoder.decode(envelope.json));
      } catch (err) {
        Sentry.captureException(err, { extra: { context: 'AssistantTransport.done_parse' } });
      }
    }

    // Clear the chatId → runId mapping so a follow-up message starts a fresh run.
    this.runByChatId.delete(matchedChatId);
    this.activeTurnChatIds.delete(matchedChatId);

    this.onConversationDone?.({
      iterations: body.iterations ?? 0,
      toolsCalled: body.toolsCalled ?? 0,
      cancelled: body.cancelled ?? false,
    });
  }

  /**
   * Connection-scoped KIND_AGENT_EVENT handler that detects tool-approval
   * interrupts in BACKGROUND runs. Fires onApprovalRequested(chatId) so the
   * consumer can raise a distinct "needs you" badge (design §8.4). The run
   * legitimately stays parked (cortex's interrupt model) — this only SURFACES
   * it; the interrupt is unchanged.
   *
   * Only acts on frames whose corrId matches a runId we own AND whose decoded
   * body is the cortex approval chunk
   * `{ type: 'tool-approval-request', approvalId, toolCallId }` (verified shape —
   * vercel_chunk_serializer.py:210-214 / approve.py:215-225). NO `state` field
   * is involved. All other event frames are ignored here (the per-turn stream
   * handles live rendering); the sibling `data-approval-detail` chunk is ignored
   * here too — the minimal chunk is sufficient to raise the badge.
   */
  private handleApprovalRequestFrame(envelope: Envelope): void {
    if (envelope.json.length === 0) return;

    // Resolve the owning chatId by corrId; bail early if we don't own it.
    const corrIdHex = bytesToUuid(envelope.corrId);
    let matchedChatId: string | undefined;
    for (const [chatId, rid] of this.runByChatId.entries()) {
      if (rid === corrIdHex) {
        matchedChatId = chatId;
        break;
      }
    }
    if (matchedChatId === undefined) return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(new TextDecoder().decode(envelope.json));
    } catch (err) {
      Sentry.captureException(err, {
        extra: { context: 'AssistantTransport.approval_request_parse', corrIdHex },
      });
      return;
    }
    const body = parsed as { type?: string; approvalId?: string } | null;
    if (
      typeof body === 'object' &&
      body !== null &&
      body.type === 'tool-approval-request' &&
      typeof body.approvalId === 'string'
    ) {
      this.onApprovalRequested?.(matchedChatId);
    }
  }

  private completeTurn(
    controller: ReadableStreamDefaultController<UIMessageChunk>,
    body: TurnCompleteBody,
    finishTurn: (controller: ReadableStreamDefaultController<UIMessageChunk>) => void,
  ): void {
    finishTurn(controller);
    // The run just spent LLM credits and nothing else refreshes the balance,
    // so the meter would otherwise show a stale number until Settings reopens.
    void invalidateCreditBalance(useWorkspaceStore.getState().activeWorkspaceId);
    this.onTurnComplete?.({
      iterations: body.iterations ?? 0,
      toolsCalled: body.toolsCalled ?? 0,
      hasPendingApproval: body.hasPendingApproval ?? false,
    });
  }
}
